import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, FlaskConical, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSession } from "@/context/SessionContext";
import { Button } from "@/components/ui/button";

const SAMPLE = [
  ["Get a free estimate today", "Obtenga un presupuesto gratis hoy"],
  ["Lawn care that fits your schedule", "Cuidado del césped que se adapta a su horario"],
  ["Book now", "Reserve ahora"],
];

const Preview = () => (
  <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-[0_20px_60px_-30px_rgba(15,23,42,0.35)] rise" style={{ animationDelay: "120ms" }}>
    <div className="grid grid-cols-2 text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-200">
      <div className="px-4 py-2 border-r border-slate-200">English · clientsite.com</div>
      <div className="px-4 py-2">Español · /es/</div>
    </div>
    {SAMPLE.map(([en, es], i) => (
      <div key={en} className="grid grid-cols-2 text-sm border-b last:border-b-0 border-slate-100 rise" style={{ animationDelay: `${200 + i * 90}ms` }}>
        <div className="px-4 py-3 border-r border-slate-100 text-slate-600">{en}</div>
        <div className="px-4 py-3 text-slate-900 font-medium">{es}</div>
      </div>
    ))}
  </div>
);

export default function Login() {
  const { session, config, signIn } = useSession();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  if (session) return <Navigate to="/" replace />;

  const demo = async () => {
    setBusy(true);
    try {
      const r = await api.post("/sso/demo");
      const { token, ...ctx } = r.data;
      signIn(token, ctx);
      navigate("/");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid-paper">
      <div className="max-w-6xl mx-auto px-6 py-10 lg:py-20 grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-20 items-center">
        <div className="rise">
          <div className="flex items-center gap-2 mb-10">
            <div className="h-8 w-8 rounded-md bg-slate-900 text-white grid place-items-center font-mono text-[11px]">es</div>
            <span className="font-display font-bold tracking-tight">Morr Translate</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.02]">
            Your website,<br /><span className="text-blue-600">en español.</span>
          </h1>
          <p className="mt-6 text-base text-slate-600 max-w-lg leading-relaxed">
            A Spanish version of each business website, for Emergent sites (<span className="font-mono text-sm">/es/</span>) and GoHighLevel funnels (<span className="font-mono text-sm">es.</span> subdomain). AI translates new text and you review it side by side.
          </p>
          <div className="mt-8 space-y-3 max-w-sm">
            <a href={config?.plans_url?.replace(/\/plans$/, "") || "#"} data-testid="morr-access-signin-link" className="block">
              <Button className="w-full h-11 justify-between bg-slate-900 hover:bg-slate-800" disabled={config?.demo_mode}>
                <span className="flex items-center gap-2"><KeyRound className="h-4 w-4" /> Open from Morr Access</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </a>
            {config?.demo_mode && (
              <>
                <Button variant="outline" className="w-full h-11 justify-between border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900" onClick={demo} disabled={busy} data-testid="demo-signin-button">
                  <span className="flex items-center gap-2"><FlaskConical className="h-4 w-4" /> {busy ? "Starting demo…" : "Try the demo"}</span>
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <p className="text-xs text-slate-500" data-testid="demo-mode-note">
                  {config?.app_key_invalid
                    ? "DEMO MODE: the saved Morr Access app key looks incomplete, so real sign-in stays off. Re-enter the full mak_ key."
                    : "DEMO MODE: Morr Access isn't connected yet (no app key). The demo uses sample businesses and simulated plan limits."}
                </p>
              </>
            )}
          </div>
          <div className="mt-12 flex gap-8 text-xs font-mono text-slate-500">
            <div><div className="text-slate-900 text-lg font-display font-bold">$12</div>per website / month</div>
            <div><div className="text-slate-900 text-lg font-display font-bold">50,000</div>words included</div>
            <div><div className="text-slate-900 text-lg font-display font-bold">$3</div>per extra 25,000</div>
          </div>
        </div>
        <Preview />
      </div>
    </div>
  );
}
